import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { TYPE, SPACING } from '../_shared';
import TrustBadges from '../_shared/TrustBadges';

/**
 * Footer v3 — light, minimal, 4-column grid.
 *
 * Industries column doubles as SEO link farm for landing pages.
 */
export default function FooterV3() {
  const t = useTranslations('landing.footerV3');
  const c = useTranslations('legal.company');

  const productLinks = [
    { key: 'features', href: '/#features' },
    { key: 'pricing', href: '/#pricing' },
    { key: 'howItWorks', href: '/#how-it-works' },
    { key: 'faq', href: '/#faq' },
    { key: 'blog', href: '/blog' },
    { key: 'calculator', href: '/zeit-spar-rechner' },
    { key: 'commissionCalculator', href: '/provisions-spar-rechner' },
  ];
  // F-500 Footer-Pattern (Stripe/Linear/Vercel): max 5-6 Items pro Spalte +
  // "Alle anzeigen"-Link auf Hub-Page. Wir zeigen 5 on-Avatar DACH-fokussierte
  // Comparisons + Hub-Link. Off-Avatar (Calendly/Cal.com) und kleinere (Belbo/
  // Acuity) leben auf /alternativen.
  const comparisonLinks = [
    { key: 'setmoreAlternative', href: '/setmore-alternative' },
    { key: 'treatwellAlternative', href: '/treatwell-alternative' },
    { key: 'freshaAlternative', href: '/fresha-alternative' },
    { key: 'planityAlternative', href: '/planity-alternative' },
    { key: 'phorestAlternative', href: '/phorest-alternative' },
  ];
  // F-500 Footer-Pattern (analog zu Comparisons): max 6 on-Avatar/Top-Volumen-
  // Branchen + "Alle Branchen ansehen"-Link auf Hub /branchen. Die übrigen 5
  // Industry-LPs (Tierarzt/Heilpraktiker/Coaching/Physio/Yoga) leben auf dem Hub.
  const industriesLinks = [
    { key: 'friseur', href: '/friseur-online-buchung' },
    { key: 'kosmetik', href: '/kosmetikstudio-terminbuchung' },
    { key: 'nails', href: '/nagelstudio-online-termine' },
    { key: 'massage', href: '/massage-terminbuchung' },
    { key: 'workshop', href: '/werkstatt-termin-online-buchen' },
    { key: 'tires', href: '/reifenwechsel-termin-online' },
  ];
  const legalLinks = [
    { key: 'about', href: '/about' },
    { key: 'imprint', href: '/impressum' },
    { key: 'privacy', href: '/privacy' },
    { key: 'terms', href: '/terms' },
    { key: 'cookies', href: '/cookies#settings' },
    { key: 'contact', href: '/kontakt' },
  ];

  return (
    <footer className="border-t border-gray-200 bg-white">
      <div className={`${SPACING.container} py-14 sm:py-16`}>
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-10">
          {/* Brand */}
          <div>
            <Link
              href="/"
              className="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 rounded-sm"
            >
              <Image
                src="/logo.svg"
                alt="EazyBooking"
                width={140}
                height={36}
                priority={false}
              />
            </Link>
            <address className="mt-5 not-italic text-sm leading-relaxed text-gray-600">
              <div className="font-medium text-gray-900">{c('name')}</div>
              <div>{c('address')}</div>
              <div>{c('city')}</div>
              <div>{c('country')}</div>
            </address>
            <ul role="list" className="mt-4 space-y-1.5 text-sm text-gray-600">
              <li>{t('brand.made')}</li>
              <li>{t('brand.gdpr')}</li>
              <li>{t('brand.international')}</li>
            </ul>
          </div>

          {/* Product */}
          <FooterColumn
            heading={t('columns.product')}
            links={productLinks.map((l) => ({
              label: t(`productLinks.${l.key}`),
              href: l.href,
            }))}
          />

          {/* Industries */}
          <FooterColumn
            heading={t('columns.industries')}
            links={[
              ...industriesLinks.map((l) => ({
                label: t(`industriesLinks.${l.key}`),
                href: l.href,
              })),
              { label: t('industriesLinks.all'), href: '/branchen' },
            ]}
          />

          {/* Comparisons — F-500: 5 on-Avatar + Hub-Link */}
          <FooterColumn
            heading={t('columns.comparisons')}
            links={[
              ...comparisonLinks.map((l) => ({
                label: t(`comparisonLinks.${l.key}`),
                href: l.href,
              })),
              { label: t('comparisonLinks.viewAll'), href: '/alternativen' },
            ]}
          />

          {/* Legal */}
          <FooterColumn
            heading={t('columns.legal')}
            links={legalLinks.map((l) => ({
              label: t(`legalLinks.${l.key}`),
              href: l.href,
            }))}
          />
        </div>

        <div className="mt-12 border-t border-gray-200 pt-8">
          <TrustBadges />
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          {t('bottom')}
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  heading,
  links,
}: {
  heading: string;
  links: { label: string; href: string }[];
}) {
  return (
    <div>
      <h3 className={`${TYPE.labelMd} text-gray-500`}>{heading}</h3>
      {/* py-2.5 gives ≥44px effective tap-target per F-500 Landing rule,
          ohne den Footer optisch zu blasen — die Klick-Zone reicht weiter
          als der Text. space-y-0 weil das Padding den Abstand jetzt macht. */}
      <ul role="list" className="mt-3 space-y-0">
        {links.map((link, i) => (
          <li key={i}>
            <Link
              href={link.href}
              className="block py-3 text-sm leading-5 text-gray-700 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 rounded-sm"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
